import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Info } from 'lucide-react';
import { Tooltip } from 'common/components/Tooltip';

import styles from './MetricCard.module.scss';

export type MetricCardTheme = {
    icon: LucideIcon;
    color: string;
    softBackground: string;
};

export type MetricCardProps = {
    id: string;
    label: string;
    theme: MetricCardTheme;
    tooltip?: string;
    subtitle?: string;
    count?: number;
    percent?: number;
    /** When set, replaces the count / percent row (e.g. avg duration). */
    primaryValue?: string;
    showPercent?: boolean;
    isSelected?: boolean;
    onClick?: (id: string) => void;
    testId?: string;
    tabId?: string;
    panelId?: string;
};

const formatCount = (value: number): string => value.toLocaleString();

export const MetricCard: React.FC<MetricCardProps> = ({
    id,
    label,
    theme,
    tooltip,
    subtitle,
    count = 0,
    percent = 0,
    primaryValue,
    showPercent = true,
    isSelected = false,
    onClick,
    testId,
    tabId,
    panelId,
}) => {
    const Icon = theme.icon;
    const interactive = typeof onClick === 'function';
    const resolvedTestId = testId ?? `metric-card-${id}`;
    const resolvedTabId = tabId ?? `metric-card-tab-${id}`;
    const resolvedPanelId = panelId ?? `metric-card-panel-${id}`;

    const className = [styles.card, interactive ? '' : styles.cardStatic, isSelected ? styles.cardSelected : '']
        .filter(Boolean)
        .join(' ');

    const cssVars = {
        '--status-color': theme.color,
        '--status-soft-bg': theme.softBackground,
    } as React.CSSProperties;

    const header = (
        <div className={styles.cardHeader}>
            <span className={styles.label}>
                <span className={styles.statusIconChip}>
                    <Icon size={14} aria-hidden='true' />
                </span>
                {label}
            </span>
            {tooltip ? (
                <Tooltip content={tooltip} maxWidth={350}>
                    <Info
                        size={14}
                        className={styles.infoIcon}
                        tabIndex={0}
                        aria-label={`${label} info`}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                    />
                </Tooltip>
            ) : null}
        </div>
    );

    const valueRow =
        primaryValue != null ? (
            <span className={styles.count}>{primaryValue}</span>
        ) : (
            <div className='flex gap-xs items-end mt-auto'>
                <span className={styles.count}>{formatCount(count)}</span>
                {showPercent ? (
                    <>
                        /<span className={styles.percent}>{percent}%</span>
                    </>
                ) : null}
            </div>
        );

    const body = (
        <>
            {header}
            {valueRow}
            {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
        </>
    );

    if (interactive) {
        return (
            <button
                type='button'
                role='tab'
                id={resolvedTabId}
                aria-selected={isSelected}
                aria-controls={resolvedPanelId}
                className={className}
                style={cssVars}
                onClick={() => onClick(id)}
                data-testid={resolvedTestId}
            >
                {body}
            </button>
        );
    }

    return (
        <article className={className} style={cssVars} data-testid={resolvedTestId} aria-label={label}>
            {body}
        </article>
    );
};
